import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { find, map, propEq } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import {
  useOrganizationLogoDelete,
  useOrganizationLogos,
} from "../cspServices";
import { useOrganizations } from "../../user-admin/userServices";
import OrganizationLogoDialog from "../edit/OrganizationLogoDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationLogosCard = ({ project }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);

  const organizationLogosResponse = useOrganizationLogos(project.projectId);
  const [organizationLogos, setOrganizationLogos] = useState([]);
  useEffect(() => {
    if (organizationLogosResponse.isSuccess) {
      setOrganizationLogos(organizationLogosResponse.data);
    }
  }, [organizationLogosResponse.isSuccess, organizationLogosResponse.data]);

  const [organizations, setOrganizations] = useState([]);
  const organizationsResponse = useOrganizations();
  useEffect(() => {
    if (
      organizationsResponse.isSuccess &&
      notBlank(organizationsResponse.data)
    ) {
      setOrganizations(organizationsResponse.data);
    }
  }, [organizationsResponse.isSuccess, organizationsResponse.data]);

  const findOrganizationName = useCallback(
    orgId => {
      if (isBlank(orgId)) {
        return null;
      }

      const organization = find(propEq("organizationId", orgId), organizations);
      return notBlank(organization) ? organization.organizationName : null;
    },
    [organizations],
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSave = useCallback(async data => {
    setDialogOpen(false);
  }, []);

  const logoDelete = useOrganizationLogoDelete();
  const handleDeleteClick = useCallback(
    async orgLogo => {
      try {
        await logoDelete.mutateAsync({
          resourceIds: {
            organizationId: orgLogo.organizationId,
            projectId: project.projectId,
          },
        });
      } catch (err) {
        alertError(err);
      }
    },
    [logoDelete, project.projectId, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Organization Logos"
        subheader="Changes here take affect immediately.  No save is necessary."
        action={
          <Tooltip title="Add Organization Logo">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {organizationLogosResponse.isLoading && <PanelLoadingIndicator />}
        {organizationLogosResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Organization Logos.
          </Box>
        )}
        {organizationLogosResponse.isSuccess && isBlank(organizationLogos) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Organization Logos are set up for this Project.
          </Box>
        )}
        {organizationLogosResponse.isSuccess && notBlank(organizationLogos) && (
          <List dense style={{ maxHeight: 350, overflowY: "auto" }}>
            {map(
              orgLogo => (
                <ListItem key={orgLogo.organizationId}>
                  <ListItemText
                    primary={findOrganizationName(orgLogo.organizationId)}
                    secondary={orgLogo.logoLocation}
                  />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(orgLogo)}
                      dialogTitle="Remove Organization Logo"
                      dialogBody="Are you sure you want to remove this organizaiton logo?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              organizationLogos,
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <OrganizationLogoDialog
          open={dialogOpen}
          projectId={project.projectId}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </Card>
  );
};

export default OrganizationLogosCard;
